import React, { useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDown, Edit, Trash2 } from 'lucide-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/pages/DeviceTableView.css';
import '../../styles/pages/alerts.css';
import { UserDto, SiteDetailDto } from '../../services/userManagementApi';
import { roleMap } from '../../utils/helperFunctions';

interface UsersTableProps {
  users: UserDto[];
  onRoleChange: (_userId: number, _role: UserDto['role']) => void;
  onStatusToggle: (_userId: number) => void;
  loggedInUserRole : string;
}

const UsersTable: React.FC<UsersTableProps> = ({ users, onRoleChange, onStatusToggle, loggedInUserRole }) => {
  const navigate = useNavigate();
  const ROLES: UserDto['role'][] = roleMap[loggedInUserRole] || [];
  const gridRef = useRef<AgGridReact>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const goToPage = (page: number) => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToPage(page);
      setCurrentPage(page);
    }
  };

  const generateColumnDefs = (): ColDef[] => [
    {
      headerName: 'Name',
      field: 'firstName',
      minWidth: 160,
      flex: 1,
      sortable: true,
      filter: false,
      resizable: false,
    },
    {
      headerName: 'Email',
      field: 'email',
      minWidth: 200,
      flex: 1.5,
      sortable: true,
      filter: false,
      resizable: false,
    },
    {
      headerName: 'Role',
      field: 'role',
      minWidth: 180,
      flex: 1,
      sortable: true,
      filter: false,
      resizable: false,
      cellRenderer: (params: any) => {
        const user: UserDto = params.data;
        return (
          <div className="flex items-center gap-1">
           {loggedInUserRole == "Super Admin" || loggedInUserRole == "Admin" ? (
            <>
              <select
                value={user.role}
                onChange={(e) => onRoleChange(user.userId, e.target.value as UserDto['role'])}
                className="bg-transparent border-none text-[var(--color-text)] text-sm font-['DM_Sans'] cursor-pointer outline-none appearance-none"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-[var(--color-sub-title-text)] pointer-events-none flex-shrink-0" />
            </>
            ) : (
              <div>{user.role}</div>
            )}
          </div>
        );
      },
    },
    {
      headerName: 'Access Sites',
      field: 'accessSites',
      minWidth: 180,
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
      wrapText: true,
      autoHeight: true,
      cellRenderer: (params: any) => {
        const sites: SiteDetailDto[] = Array.isArray(params.data.accessSites) ? params.data.accessSites : [];
        if (sites.length === 0) return <span style={{ color: 'var(--color-sub-title-text)' }}>---</span>;
        return <span>{sites.map((s) => s?.siteName).filter(Boolean).join(', ')}</span>;
      },
    },
    {
      headerName: 'Status',
      field: 'isActive',
      minWidth: 140,
      flex: 1,
      sortable: true,
      filter: false,
      resizable: false,
      cellRenderer: (params: any) => {
        const user: UserDto = params.data;
        return (
          <div className="flex items-center gap-2">
            <button
              className={`status-toggle ${user.isActive ? 'active' : 'inactive'}`}
              onClick={() => onStatusToggle(user.userId)}
            >
              <div className="toggle-slider" />
            </button>
            <span className={user.isActive ? 'text-[#217C70] text-sm font-medium' : 'text-[var(--color-sub-title-text)] text-sm font-medium'}>
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (params: any) => {
        const user: UserDto = params.data;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/edit-user/${user.userId}`)}
              className="alerts-action-button edit"
              title="Edit User"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              // onClick={() => onDelete(user)}
              className="alerts-action-button delete"
              title="Delete User"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
      flex: 1,
      sortable: false,
      filter: false,
    },
  ];

  const columnDefs: ColDef[] = useMemo(() => generateColumnDefs(), [users, onRoleChange, onStatusToggle]);

  const gridOptions: GridOptions = useMemo(() => ({
    defaultColDef: {
      resizable: false,
      sortable: true,
      filter: false,
    },
    rowData: users,
    columnDefs,
    pagination: true,
    paginationPageSize: 5,
    suppressPaginationPanel: true,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    suppressRowDeselection: true,
    suppressDragLeaveHidesColumns: true,
    suppressColumnMoveAnimation: true,
    domLayout: 'normal',
    rowHeight: 60,
    headerHeight: 70,
    overlayNoRowsTemplate: '<span class="custom-no-rows">No users found</span>',
    icons: {
      sortAscending: '<span style="font-size:12px;">▲</span>',
      sortDescending: '<span style="font-size:12px;">▼</span>',
    },
  }), [columnDefs, users]);

  return (
    <div className="ag-theme-alpine w-full flex flex-col flex-1">
      <AgGridReact
        ref={gridRef}
        {...gridOptions}
        className="device-table-grid zoomOutLevel"
        onPaginationChanged={() => {
          if (gridRef.current?.api) {
            setCurrentPage(gridRef.current.api.paginationGetCurrentPage());
            setTotalPages(gridRef.current.api.paginationGetTotalPages());
          }
        }}
      />

      {totalPages > 1 && (
        <div className="pagination-container h-auto zoomOutLevel">
          <button
            disabled={currentPage <= 0}
            className="pagination-button pagination-previous"
            onClick={() => goToPage(currentPage - 1)}
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => goToPage(i)}
              className={`pagination-button ${currentPage === i ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages - 1}
            className="pagination-button pagination-next"
            onClick={() => goToPage(currentPage + 1)}
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default UsersTable;
